import { Injectable } from '@angular/core';
import { XYZModel } from '../shared/models/xyz.model';
import { HitModel } from './models/hit.model';
import { CameraModel } from './models/camera.model';
import { NumberService } from '../shared/number.service';
import { Observable } from 'rxjs';
import { BoundaryModel } from './models/boundary.model';
import { vec3 } from 'gl-matrix';
import { HumanInterface } from './models/human.interface';

declare const Human: HumanInterface;
const epsilon = 0.001;
const zoomRate = 0.03;

@Injectable({
  providedIn: 'root',
})
export class CameraService {
  rotation = { vx: 0, vy: 0 };
  pan = { x: 0, y: 0, z: 0 };
  zoom = 0;

  initCamera: Function;

  constructor(private numberService: NumberService) {}

  getEye(): XYZModel {
    return Human.renderer.camera.eye;
  }

  setEye(eye: XYZModel) {
    Human.renderer.camera.setLookAt({
      eye,
    });
  }

  getCamera(): CameraModel {
    return Human.renderer.camera.getLookAt();
  }

  getLookDirection(): Float32Array | [number, number, number] {
    const dir = vec3.create();
    Human.renderer.camera.getLookDir(dir);
    return dir;
  }

  setCamera(camera: CameraModel) {
    return Human.renderer.camera.setLookAt(camera);
  }

  getCameraZoom(): number {
    return Human.renderer.camera.getZoom();
  }

  captureCamera(): { lookAt: CameraModel; zoom: number } {
    return {
      lookAt: Human.renderer.camera.getLookAt(),
      zoom: Human.renderer.camera.getZoom(),
    };
  }

  resetCamera() {
    return new Observable((observer) => {
      Human.init.resetCamera(() => {
        observer.next();
        observer.complete();
      });
    });
  }

  getDirection(hit: HitModel) {
    const camera = this.getEye();
    const direction = vec3.create();
    direction[0] = camera.x - hit.worldPos[0];
    direction[1] = camera.y - hit.worldPos[1];
    direction[2] = camera.z - hit.worldPos[2];
    vec3.normalize(direction, direction);

    return direction;
  }

  rotateCameraStatic(x: number, y: number) {
    Human.renderer.camera.rotateX(x);
    Human.renderer.camera.rotateY(y);
  }

  panCameraStatic(x: number, y: number, z: number) {
    Human.renderer.camera.pan({ x, y, z });
  }

  setHumanZoom(delta: number) {
    Human.renderer.camera.zoom(delta);
  }

  setHumanZoomFactor(zoomFactor: number) {
    Human.renderer.camera.setZoom(zoomFactor);
  }

  setCameraRotation(velocityX: number, velocityY: number) {
    this.rotation.vx = velocityY;
    this.rotation.vy = -velocityX;
  }

  setCameraPan(velocityX: number, velocityY: number) {
    this.pan.x = velocityX;
    this.pan.y = velocityY;
  }

  setCameraZoom(deltaY: number) {
    this.zoom = deltaY * zoomRate;
  }

  setCameraZoomWithVelocity(deltaY: number) {
    this.setCameraZoom(deltaY);

    requestAnimationFrame(() => {
      deltaY *= 0.85;
      if (Math.abs(deltaY) > 1) {
        this.setCameraZoomWithVelocity(deltaY);
      }
    });
  }

  tickCameraRotation() {
    this.rotation.vx = this.checkEpsilon(this.rotation.vx);
    this.rotation.vy = this.checkEpsilon(this.rotation.vy);

    return this.rotation;
  }

  tickCameraPan() {
    this.pan.x = this.checkEpsilon(this.pan.x);
    this.pan.y = this.checkEpsilon(this.pan.y);
    this.pan.z = this.checkEpsilon(this.pan.z);

    return this.pan;
  }

  tickCameraZoom() {
    this.zoom = this.checkEpsilon(this.zoom);

    return this.zoom;
  }

  /**
   * Returns if the cameras are equivalent.
   */
  areCamerasEqual(camera1: CameraModel, camera2: CameraModel): boolean {
    return (
      this.compareXYZ(camera1.eye, camera2.eye) &&
      this.compareXYZ(camera1.look, camera2.look) &&
      this.compareXYZ(camera1.up, camera2.up)
    );
  }

  /**
   * Returns if the XYZ coordinates are equal.
   */
  compareXYZ(model1: XYZModel, model2: XYZModel): boolean {
    return model1.x === model2.x && model1.y === model2.y && model1.z === model2.z;
  }

  /**
   * Rounds the camera to the specified precision.
   */
  roundCamera(camera: CameraModel, precision: number): CameraModel {
    const { eye, look, up } = camera;
    return {
      eye: this.numberService.roundModel<XYZModel>(eye, precision),
      look: this.numberService.roundModel<XYZModel>(look, precision),
      up: this.numberService.roundModel<XYZModel>(up, precision),
    };
  }

  /**
   * Ported from engine flyAnimation.js
   * Pure fn that returns the resulting CameraModel after a flyTo to an object's boundary.
   * @param objectBoundary found by object.getBoundary()
   */
  cameraAfterFlyTo(objectBoundary: BoundaryModel): CameraModel {
    const startCamera = this.getCamera();
    const _look1 = XYZModel.toArray(startCamera.look);
    const _eye1 = XYZModel.toArray(startCamera.eye);

    const tempVec = vec3.create();
    vec3.subtract(tempVec, _eye1, _look1);

    const _vec = vec3.create();
    vec3.normalize(_vec, tempVec);

    const stopFOV = 55;
    const backOff = 1.0;
    const dist = 2.5;
    // ?? : this._vec is normalized, this might be changed to lenVec = 1
    const lenVec = Math.abs(vec3.length(_vec));
    const diag = Human.math.getBoundaryDiag(objectBoundary);
    const len = Math.abs(diag / (1.0 + backOff * 0.8) / Math.tan(stopFOV / 2)); /// Tweak this to set final camera distance on arrival
    const sca = (len / lenVec) * dist;
    const _look2 = Human.math.getBoundaryCenter(objectBoundary);

    vec3.scale(tempVec, _vec, sca);
    const _eye2 = vec3.add(vec3.create(), _look2, tempVec);

    return {
      eye: XYZModel.fromArray(_eye2),
      look: XYZModel.fromArray(_look2),
      up: { x: 0, y: 1, z: 0 },
    };
  }

  setChapterCameraEnabled(enabled: boolean) {
    if (!this.initCamera) {
      this.initCamera = Human.timeline.BranchTimeline.prototype.initCamera;
    }

    if (enabled) {
      Human.timeline.BranchTimeline.prototype.initCamera = this.initCamera;
    } else {
      Human.timeline.BranchTimeline.prototype.initCamera = () => {};
    }
  }

  private checkEpsilon(value: number) {
    return Math.abs(value) < epsilon ? 0 : value;
  }
}
